import {
  case_queue_metrics,
  fetch_case_owner_cases,
  fetch_case_owner_status_items,
  filter_cases_by_status,
} from './owner_case_queue_service';
import { company_cases_creation_methods } from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_creation';
import BlockingLoaderOverlay from '@/components/config_components/BlockingLoaderOverlay/BlockingLoaderOverlay.vue';
import { DEFAULT_NEW_CASE_FORM } from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_helpers';
import { read_session_project_id } from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_common';
import {
  fetch_scoped_entity_types,
  get_entity_type_name,
  normalize_key,
} from '@/views/x-verdict_views/CompanyAdmin/CompanyCases/company_cases_helpers';

const CASE_FILTERS = [
  { key: 'all', label: 'All domains' },
  { key: 'high-risk', label: 'High Risk' },
  { key: 'sla-breached', label: 'SLA Breached' }
];

function case_detail_query(case_item) {
  if (!case_item || typeof case_item === 'string') return {};

  return Object.fromEntries([
    ['application_id', case_item.application_id],
    ['entity_profile_id', case_item.entity_profile_id],
    ['entity_type_id', case_item.entity_type_id],
    ['risk_assessment_id', case_item.risk_assessment_id]
  ].filter((entry) => Boolean(entry[1])));
}

export default {
  name: 'OwnerAllCases',
  components: {
    BlockingLoaderOverlay,
  },
  data: () => ({
    active_filter: 'all',
    filters: CASE_FILTERS,
    cases: [],
    case_types: [],
    workflow_status_filter_options: [],
    loading_cases: false,
    cases_error: '',
    show_new_case_modal: false,
    new_case_form: { ...DEFAULT_NEW_CASE_FORM },
    new_case_error: '',
    submitting_new_case: false,
    onboarding_preview: null,
    onboarding_preview_loading: false,
    onboarding_preview_error: '',
    onboarding_execution: null,
    onboarding_status: null,
    created_case: null,
  }),
  created() {
    this.apply_route_filter();
    this.load_cases();
  },
  computed: {
    project_id() {
      return this.$route?.query?.project_id
        || this.$route?.query?.projectId
        || read_session_project_id();
    },
    active_status_filter() {
      return String(this.$route.query?.status || '').trim().toLowerCase();
    },
    metrics() {
      return case_queue_metrics(this.filtered_cases);
    },
    display_filters() {
      const entity_type_filters = this.case_types
        .map((entity_type) => {
          const label = get_entity_type_name(entity_type);
          return label
            ? { key: normalize_key(label), label }
            : null;
        })
        .filter(Boolean);

      return [
        { key: 'all', label: 'All domains' },
        ...entity_type_filters,
        { key: 'high-risk', label: 'High Risk' },
        { key: 'sla-breached', label: 'SLA Breached' }
      ];
    },
    display_status_filters() {
      return [
        { key: 'all', label: 'Filter by status' },
        ...this.workflow_status_filter_options,
      ];
    },
    selected_status_filter() {
      return this.display_status_filters.find((item) => item.key === this.active_status_filter)?.key || 'all';
    },
    new_case_domain_options() {
      return this.case_types
        .map((entity_type) => ({
          id: Number(entity_type?.id),
          label: get_entity_type_name(entity_type) || `Entity type ${entity_type?.id}`,
        }))
        .filter((entity_type) => entity_type.id);
    },
    modal_loading() {
      return this.show_new_case_modal && (this.onboarding_preview_loading || this.submitting_new_case);
    },
    modal_loading_message() {
      if (this.submitting_new_case) {
        return 'Creating case and running onboarding...';
      }

      return 'Preparing onboarding preview...';
    },
    modal_loading_detail() {
      if (this.submitting_new_case) {
        return 'Please wait while we create the case and start the workflow.';
      }

      return 'Loading the requestor experience for the selected domain.';
    },
    onboarding_preview_forms() {
      return Array.isArray(this.onboarding_preview?.forms)
        ? this.onboarding_preview.forms
        : [];
    },
    onboarding_completion() {
      return this.onboarding_status?.completion || {};
    },
    is_email_source() {
      return String(this.new_case_form.source_channel || '').toLowerCase() === 'email';
    },
    can_create_new_case() {
      return Boolean(this.new_case_form.entity_type_id);
    },
    filtered_cases() {
      let filtered = this.cases.slice();

      if (this.active_filter === 'high-risk') {
        filtered = filtered.filter((item) => item.risk_label === 'High');
      } else if (this.active_filter === 'sla-breached') {
        filtered = filtered.filter((item) => String(item.sla || '').toLowerCase().includes('overdue'));
      } else if (this.active_filter !== 'all') {
        filtered = filtered.filter((item) => item.domain_key === this.active_filter);
      }

      return filter_cases_by_status(
        filtered,
        this.selected_status_filter === 'all' ? '' : this.selected_status_filter
      );
    }
  },
  methods: {
    async load_cases() {
      this.loading_cases = true;
      this.cases_error = '';

      try {
        const [cases, entity_type_result, status_items] = await Promise.all([
          fetch_case_owner_cases(this.project_id),
          fetch_scoped_entity_types(this.project_id),
          fetch_case_owner_status_items(this.project_id),
        ]);

        this.cases = cases;
        this.case_types = Array.isArray(entity_type_result?.entity_types)
          ? entity_type_result.entity_types
          : [];
        this.workflow_status_filter_options = Array.isArray(status_items)
          ? status_items.map((item) => ({
            key: String(item?.route_query?.status || '').trim().toLowerCase(),
            label: item?.label || '',
          })).filter((item) => item.key && item.label)
          : [];

        if (!this.new_case_form.entity_type_id && this.new_case_domain_options[0]?.id) {
          this.new_case_form.entity_type_id = String(this.new_case_domain_options[0].id);
        }
      } catch (error) {
        this.cases = [];
        this.case_types = [];
        this.workflow_status_filter_options = [];
        this.cases_error = error?.response?.data?.message || error?.message || 'Failed to load cases.';
      } finally {
        this.loading_cases = false;
      }
    },
    apply_route_filter() {
      const route_filter = String(this.$route.query?.filter || '').trim().toLowerCase();
      this.active_filter = this.display_filters.some((item) => item.key === route_filter)
        ? route_filter
        : 'all';
    },
    set_filter(filter_key) {
      this.active_filter = filter_key;
      const next_query = { ...this.$route.query };

      if (filter_key === 'all') {
        delete next_query.filter;
      } else {
        next_query.filter = filter_key;
      }

      this.$router.replace({
        name: this.$route.name,
        query: next_query
      });
    },
    set_status_filter(status_key) {
      const next_query = { ...this.$route.query };
      const normalized_status = String(status_key || '').trim().toLowerCase();

      if (!normalized_status || normalized_status === 'all') {
        delete next_query.status;
      } else {
        next_query.status = normalized_status;
      }

      this.$router.replace({
        name: this.$route.name,
        query: next_query
      });
    },
    async load_company_cases() {
      await this.load_cases();
    },
    go_to_case_detail(case_item) {
      const case_code = typeof case_item === 'string' ? case_item : case_item?.case_code;
      this.$router.push({
        name: 'ViewCaseDetails',
        params: { caseCode: case_code },
        query: case_detail_query(case_item),
      });
    },
    ...company_cases_creation_methods,
  },
  watch: {
    '$route.query.filter'() {
      this.apply_route_filter();
    },
    case_types() {
      this.apply_route_filter();
    },
    project_id() {
      this.load_cases();
    }
  }
};
